import { api } from './api';
import {
  ForgotPasswordParams,
  LoginParams,
  ResetPasswordParams,
  SignupParams,
  TokenValidationParams,
} from './auth-api.types';

/**
 * Handles API requests with error handling.
 * Ensures all API responses return `{ data, error }`.
 */
const handleApiRequest = async <T>(apiCall: Promise<T>) => {
  try {
    const data = await apiCall;
    return { data, error: null };
  } catch (error) {
    console.error('API Request Failed:', error);
    return { data: null, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/**
 * Fixes token encoding issues.
 */
const fixTokenEncoding = (token: string) => decodeURIComponent(token.replace(/ /g, '+'));

/**
 * Authenticates user and retrieves access token.
 */
export const login = (params: LoginParams) => {
  const data = new URLSearchParams(params).toString();
  return handleApiRequest(
    api.create('/connect/token', data, {
      headers: {
        Accept: '*/*',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
  );
};

/**
 * Logs out the current user.
 */
export const logout = () => handleApiRequest(api.create('/logout/', {}));

/**
 * Registers a new user.
 */
export const signup = (params: SignupParams) => handleApiRequest(api.create('/register/', params));

/**
 * Initiates password reset process.
 */
export const forgotPassword = (params: ForgotPasswordParams) =>
  handleApiRequest(api.create('/api/email/resetPassword/', params));

/**
 * Resets user password with token.
 */
export const resetPassword = ({ token, ...params }: ResetPasswordParams) =>
  handleApiRequest(
    api.update(`/api/Account/users/resetPassword/${params.id}`, {
      ...params,
      token: fixTokenEncoding(token),
    })
  );

/**
 * Validates password reset token.
 */
export const validateToken = ({ token, userId }: TokenValidationParams) =>
  handleApiRequest(
    api.create('api/Account/users/validateResetPassToken', {
      token: fixTokenEncoding(token),
      userId,
    })
  );

/**
 * Confirms new user registration.
 */
export const confirmUser = (token: string, userId: string) =>
  handleApiRequest(
    api.create(`api/Account/users/confirmUser/${userId}`, {
      token: fixTokenEncoding(token),
      userId,
    })
  );
